/**
 * InvoiceReviewPanel — 单张账单审核的入口组件
 *
 * 替代旧的 APReconciliationPage，核心变化：
 *   1. 去掉 <Layout style={{ minHeight: "100vh" }}> — 纯嵌入式，不再假设独立页面
 *   2. 去掉 embedded 参数 — 始终以嵌入模式运行
 *   3. 使用 InvoiceReviewProvider 替代 APReconciliationProvider
 *   4. 引用 review/ 目录下的新组件（与旧目录完全隔离）
 *   5. 新增 onStatusChange 回调 — 通过 Context 透传给 BatchContext
 */

import React, { useState, useCallback, useMemo } from "react";
import { Drawer, Button, Badge, Tooltip } from "antd";
import { ToolOutlined, CloseOutlined } from "@ant-design/icons";
import { InvoiceReviewProvider, useInvoiceReview } from "../../context/InvoiceReviewContext";
import ReconciliationWorkspace from "./ReconciliationWorkspace";
import ReconciliationTools from "./ReconciliationTools";
import DocumentViewer from "./DocumentViewer";
import SplitViewModal from "./SplitViewModal";
import ReconciliationHeader from "./ReconciliationHeader";

/**
 * 内部组件 — 在 InvoiceReviewProvider 内使用 Context
 */
const InvoiceReviewInner = ({ onSplitViewChange, onCloseDrawer, drawerRecord }) => {
    const { selectedLineItem, clearSelectedLineItem } = useInvoiceReview();

    // 核对助手 Drawer 状态
    const [drawerVisible, setDrawerVisible] = useState(false);

    // 文档查看器状态
    const [documentViewerState, setDocumentViewerState] = useState({
        visible: false,
        type: null,
    });

    // 选中行项目时自动打开核对助手 Drawer
    const hasSelectedItem = Boolean(selectedLineItem && selectedLineItem.invoiceIndex !== null);
    React.useEffect(() => {
        if (hasSelectedItem) {
            setDrawerVisible(true);
        }
    }, [hasSelectedItem, selectedLineItem?.invoiceIndex, selectedLineItem?.sectionIndex, selectedLineItem?.itemIndex]);

    const handleDrawerClose = () => {
        setDrawerVisible(false);
        setTimeout(() => {
            clearSelectedLineItem();
        }, 300);
    };

    const toggleDocumentViewer = (type) => {
        setDocumentViewerState((prev) => ({
            visible: prev.type === type ? !prev.visible : true,
            type: type,
        }));
    };

    const closeDocumentViewer = () => {
        setDocumentViewerState({ visible: false, type: null });
    };

    // 分屏视图
    const [splitViewVisible, setSplitViewVisible] = useState(false);
    const toggleSplitView = useCallback(() => {
        setSplitViewVisible((prev) => !prev);
    }, []);

    // 分屏状态变化时通知父级（用于联动控制父级 Drawer 的显隐）
    React.useEffect(() => {
        onSplitViewChange?.(splitViewVisible);
    }, [splitViewVisible, onSplitViewChange]);

    const drawerTitle = useMemo(() => {
        return "核对助手";
    }, []);

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#f5f5f5" }}>
            {/* 头部 */}
            <ReconciliationHeader
                toggleDocumentViewer={toggleDocumentViewer}
                documentViewerState={documentViewerState}
                embedded={true}
                toggleSplitView={toggleSplitView}
                splitViewVisible={splitViewVisible}
                onCloseDrawer={onCloseDrawer}
                drawerRecord={drawerRecord}
            />

            {/* 主内容区域 */}
            <div style={{ flex: 1, padding: "16px", overflow: "auto" }}>
                <div
                    style={{
                        background: "#fff",
                        borderRadius: "8px",
                        border: "1px solid #f0f0f0",
                        minHeight: "calc(100% - 32px)",
                    }}
                >
                    <ReconciliationWorkspace />
                </div>

                {/* 浮动打开核对助手按钮 */}
                {!drawerVisible && (
                    <Tooltip title="打开核对助手" placement="left">
                        <Button
                            type="primary"
                            shape="circle"
                            size="large"
                            icon={
                                <Badge dot={hasSelectedItem} offset={[-2, 2]}>
                                    <ToolOutlined style={{ color: "#fff" }} />
                                </Badge>
                            }
                            onClick={() => setDrawerVisible(true)}
                            style={{
                                position: "fixed",
                                right: 24,
                                bottom: 24,
                                width: 48,
                                height: 48,
                                zIndex: 100,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            }}
                        />
                    </Tooltip>
                )}
            </div>

            {/* 核对助手 Drawer */}
            <Drawer
                title={drawerTitle}
                placement="right"
                width={520}
                onClose={handleDrawerClose}
                open={drawerVisible}
                mask={true}
                styles={{
                    body: { padding: 0 },
                    header: { borderBottom: "1px solid #f0f0f0" },
                    mask: { background: "rgba(0, 0, 0, 0.25)" },
                }}
                closeIcon={<CloseOutlined />}
            >
                <ReconciliationTools />
            </Drawer>

            {/* 凭证查看器 */}
            <DocumentViewer visible={documentViewerState.visible} type={documentViewerState.type} onClose={closeDocumentViewer} />

            {/* 分屏视图 */}
            <SplitViewModal visible={splitViewVisible} onClose={() => setSplitViewVisible(false)} toggleDocumentViewer={toggleDocumentViewer} documentViewerState={documentViewerState} embedded={true} />
        </div>
    );
};

/**
 * InvoiceReviewPanel — 入口组件
 *
 * @param {string} invoiceDataSourceId - 账单数据源 UUID（必填）
 * @param {Function} [onStatusChange] - 状态变更回调（通知 BatchContext 刷新）
 * @param {Function} [onSplitViewChange] - 分屏状态回调
 * @param {Function} [onCloseDrawer] - 关闭 Drawer 回调
 * @param {Object} [drawerRecord] - 当前 Drawer 记录数据
 */
const InvoiceReviewPanel = ({ invoiceDataSourceId, onStatusChange, onSplitViewChange, onCloseDrawer, drawerRecord }) => {
    return (
        <InvoiceReviewProvider invoiceDataSourceId={invoiceDataSourceId} onStatusChange={onStatusChange}>
            <InvoiceReviewInner onSplitViewChange={onSplitViewChange} onCloseDrawer={onCloseDrawer} drawerRecord={drawerRecord} />
        </InvoiceReviewProvider>
    );
};

export default InvoiceReviewPanel;
